import * as React from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Tabs,
  Tab,
  Stack,
  Chip,
  Alert,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper,
  Grid,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import MainLayout from '../components/layout/Main';

// Icons
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import DashboardIcon from '@mui/icons-material/Dashboard';
import AssessmentIcon from '@mui/icons-material/Assessment';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import TipsAndUpdatesIcon from '@mui/icons-material/TipsAndUpdates';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CircleIcon from '@mui/icons-material/Circle';

function TabPanel(props) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`help-tabpanel-${index}`}
      aria-labelledby={`help-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
    </div>
  );
}

function Help() {
  const [tabValue, setTabValue] = React.useState(0);
  const theme = useTheme();
  const { t } = useTranslation();

  React.useEffect(() => {
    document.title = t('help.page_title', 'Help & Guide');
  }, [t]);

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  // Color reference data
  const stressLevels = [
    {
      color: '#4caf50',
      range: '0-5%',
      label: t('help.stress_balanced', 'Balanced'),
      description: t('help.stress_balanced_desc', 'Normal operation, no stress detected'),
    },
    {
      color: '#fbc02d',
      range: '5-52.75%',
      label: t('help.stress_intensive', 'Intensive'),
      description: t('help.stress_intensive_desc', 'Moderate stress, monitor closely'),
    },
    {
      color: '#f44336',
      range: '52.75-100%',
      label: t('help.stress_dangerous', 'Dangerous'),
      description: t('help.stress_dangerous_desc', 'High stress, immediate attention required'),
    },
  ];

  return (
    <MainLayout>
      <Box sx={{ width: '100%', maxWidth: 1400, mx: 'auto', p: 3 }}>
        {/* Header */}
        <Stack direction="row" alignItems="center" spacing={2} mb={3}>
          <InfoOutlinedIcon sx={{ fontSize: 40, color: theme.palette.primary.main }} />
          <Box>
            <Typography variant="h4" fontWeight="bold">
              {t('help.page_title', 'Help & Guide')}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t('help.subtitle', 'Learn how to use Safety24 effectively')}
            </Typography>
          </Box>
        </Stack>

        {/* Tabs */}
        <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
          <Tabs value={tabValue} onChange={handleTabChange} aria-label="help tabs">
            <Tab label={t('help.tab_overview', 'Overview')} icon={<InfoOutlinedIcon />} iconPosition="start" />
            <Tab label={t('help.tab_dashboard', 'Dashboard')} icon={<DashboardIcon />} iconPosition="start" />
            <Tab label={t('help.tab_detailed', 'Detailed View')} icon={<AssessmentIcon />} iconPosition="start" />
            <Tab label={t('help.tab_tips', 'Tips & Best Practices')} icon={<TipsAndUpdatesIcon />} iconPosition="start" />
          </Tabs>
        </Box>

        {/* Tab 1: Overview */}
        <TabPanel value={tabValue} index={0}>
          <Grid container spacing={3}>
            {/* Introduction */}
            <Grid item xs={12}>
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    {t('help.welcome_title', 'Welcome to Safety24')}
                  </Typography>
                  <Typography variant="body1" paragraph>
                    {t('help.welcome_text', 'Safety24 is a comprehensive vehicle load monitoring system that tracks and analyzes stress levels on your vehicles in real-time.')}
                  </Typography>
                  <Alert severity="info" sx={{ mt: 2 }}>
                    {t('help.quick_start', 'Quick Start: Select a vehicle from the dropdown, check the dashboard for alerts, and click "Detailed view" for in-depth analysis.')}
                  </Alert>
                </CardContent>
              </Card>
            </Grid>

            {/* Stress Levels */}
            <Grid item xs={12}>
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    {t('help.stress_levels_title', 'Understanding Stress Levels')}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" paragraph>
                    {t('help.stress_levels_subtitle', 'Safety24 uses a color-coded system to indicate stress levels')}
                  </Typography>
                  
                  <Stack spacing={2} mt={2}>
                    {stressLevels.map((level, index) => (
                      <Paper key={index} elevation={0} sx={{ p: 2, border: `2px solid ${level.color}20`, bgcolor: `${level.color}08` }}>
                        <Stack direction="row" spacing={2} alignItems="center">
                          <Box
                            sx={{
                              width: 60,
                              height: 60,
                              bgcolor: level.color,
                              borderRadius: 2,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                            }}
                          >
                            <Typography variant="h6" color="white" fontWeight="bold">
                              {level.range.split('-')[0]}
                            </Typography>
                          </Box>
                          <Box flex={1}>
                            <Stack direction="row" spacing={1} alignItems="center" mb={0.5}>
                              <Typography variant="h6" fontWeight="bold">
                                {level.label}
                              </Typography>
                              <Chip label={level.range} size="small" />
                            </Stack>
                            <Typography variant="body2" color="text.secondary">
                              {level.description}
                            </Typography>
                          </Box>
                        </Stack>
                      </Paper>
                    ))}
                  </Stack>
                </CardContent>
              </Card>
            </Grid>

            {/* Screenshot placeholder */}
            <Grid item xs={12}>
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    {t('help.color_examples', 'Color Examples in Action')}
                  </Typography>
                  <Box
                    sx={{
                      width: '100%',
                      height: 300,
                      bgcolor: theme.palette.grey[100],
                      borderRadius: 2,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      border: `2px dashed ${theme.palette.grey[300]}`,
                    }}
                  >
                    <Typography color="text.secondary">
                      {t('help.screenshot_placeholder', '[Screenshot: Dashboard with color-coded data]')}
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </TabPanel>

        {/* Tab 2: Dashboard */}
        <TabPanel value={tabValue} index={1}>
          <Grid container spacing={3}>
            {/* Quick Stats */}
            <Grid item xs={12}>
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    {t('help.quick_stats_title', 'Quick Stats Cards')}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" paragraph>
                    {t('help.quick_stats_desc', 'Four informative cards display key metrics for the selected vehicle')}
                  </Typography>
                  
                  <List>
                    <ListItem>
                      <ListItemIcon>
                        <CheckCircleIcon color="primary" />
                      </ListItemIcon>
                      <ListItemText
                        primary={t('help.total_sessions', 'Total Sessions')}
                        secondary={t('help.total_sessions_desc', 'Total number of recording sessions')}
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon>
                        <CheckCircleIcon color="primary" />
                      </ListItemIcon>
                      <ListItemText
                        primary={t('help.total_usage', 'Total Usage Time')}
                        secondary={t('help.total_usage_desc', 'Combined time the vehicle has been in use')}
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon>
                        <CheckCircleIcon color="primary" />
                      </ListItemIcon>
                      <ListItemText
                        primary={t('help.avg_stress', 'Average Stress Level')}
                        secondary={t('help.avg_stress_desc', 'Mean stress percentage across all sessions (color-coded)')}
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon>
                        <CheckCircleIcon color="primary" />
                      </ListItemIcon>
                      <ListItemText
                        primary={t('help.alerts', 'Alerts')}
                        secondary={t('help.alerts_desc', 'Number of stress alert events')}
                      />
                    </ListItem>
                  </List>

                  {/* Screenshot placeholder */}
                  <Box
                    sx={{
                      width: '100%',
                      height: 200,
                      bgcolor: theme.palette.grey[100],
                      borderRadius: 2,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      border: `2px dashed ${theme.palette.grey[300]}`,
                      mt: 2,
                    }}
                  >
                    <Typography color="text.secondary">
                      {t('help.screenshot_placeholder', '[Screenshot: Quick Stats Cards]')}
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            {/* Calendar */}
            <Grid item xs={12}>
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    {t('help.calendar_title', 'Usage Calendar')}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" paragraph>
                    {t('help.calendar_desc', 'The calendar shows color-coded daily stress levels. Hover over any day for detailed information.')}
                  </Typography>
                  
                  <Alert severity="info" sx={{ mb: 2 }}>
                    {t('help.calendar_tip', 'Tip: Use the arrow buttons to navigate between months. Days with warning icons indicate abnormally high usage.')}
                  </Alert>

                  {/* Screenshot placeholder */}
                  <Box
                    sx={{
                      width: '100%',
                      height: 300,
                      bgcolor: theme.palette.grey[100],
                      borderRadius: 2,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      border: `2px dashed ${theme.palette.grey[300]}`,
                    }}
                  >
                    <Typography color="text.secondary">
                      {t('help.screenshot_placeholder', '[Screenshot: Usage Calendar with tooltip]')}
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            {/* Prediction */}
            <Grid item xs={12} md={6}>
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    {t('help.prediction_title', 'Stress Pattern Prediction')}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" paragraph>
                    {t('help.prediction_desc', 'AI-powered predictions showing trends and peak stress hours based on historical data.')}
                  </Typography>
                  
                  <List dense>
                    <ListItem>
                      <ListItemIcon><CircleIcon sx={{ fontSize: 8 }} /></ListItemIcon>
                      <ListItemText primary={t('help.prediction_trend', 'Trend Analysis (Increasing/Decreasing/Stable)')} />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon><CircleIcon sx={{ fontSize: 8 }} /></ListItemIcon>
                      <ListItemText primary={t('help.prediction_peak', 'Peak Stress Hours')} />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon><CircleIcon sx={{ fontSize: 8 }} /></ListItemIcon>
                      <ListItemText primary={t('help.prediction_confidence', 'Prediction Confidence Level')} />
                    </ListItem>
                  </List>

                  {/* Screenshot placeholder */}
                  <Box
                    sx={{
                      width: '100%',
                      height: 200,
                      bgcolor: theme.palette.grey[100],
                      borderRadius: 2,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      border: `2px dashed ${theme.palette.grey[300]}`,
                      mt: 2,
                    }}
                  >
                    <Typography color="text.secondary">
                      {t('help.screenshot_placeholder', '[Screenshot: Prediction Panel]')}
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            {/* Activity Feed */}
            <Grid item xs={12} md={6}>
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    {t('help.activity_title', 'Activity Feed')}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" paragraph>
                    {t('help.activity_desc', 'Shows recent session activities and alerts. Click "Refresh" to update.')}
                  </Typography>
                  
                  <Stack spacing={1} mt={2}>
                    <Paper elevation={0} sx={{ p: 1.5, bgcolor: '#f4433610', border: '1px solid #f4433620' }}>
                      <Typography variant="body2" fontWeight="bold">
                        🔴 {t('help.activity_high', 'High Stress Alert')}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {t('help.activity_high_desc', 'Dangerous stress levels detected')}
                      </Typography>
                    </Paper>
                    <Paper elevation={0} sx={{ p: 1.5, bgcolor: '#fbc02d10', border: '1px solid #fbc02d20' }}>
                      <Typography variant="body2" fontWeight="bold">
                        🟡 {t('help.activity_moderate', 'Moderate Stress')}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {t('help.activity_moderate_desc', 'Intensive stress detected')}
                      </Typography>
                    </Paper>
                    <Paper elevation={0} sx={{ p: 1.5, bgcolor: '#4caf5010', border: '1px solid #4caf5020' }}>
                      <Typography variant="body2" fontWeight="bold">
                        🟢 {t('help.activity_session', 'Session Started')}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {t('help.activity_session_desc', 'New recording session began')}
                      </Typography>
                    </Paper>
                  </Stack>

                  {/* Screenshot placeholder */}
                  <Box
                    sx={{
                      width: '100%',
                      height: 150,
                      bgcolor: theme.palette.grey[100],
                      borderRadius: 2,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      border: `2px dashed ${theme.palette.grey[300]}`,
                      mt: 2,
                    }}
                  >
                    <Typography color="text.secondary">
                      {t('help.screenshot_placeholder', '[Screenshot: Activity Feed]')}
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </TabPanel>

        {/* Tab 3: Detailed View */}
        <TabPanel value={tabValue} index={2}>
          <Grid container spacing={3}>
            {/* Main Chart */}
            <Grid item xs={12}>
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    {t('help.chart_title', 'Stress Level Chart')}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" paragraph>
                    {t('help.chart_desc', 'The main chart shows minute-by-minute stress levels throughout the session.')}
                  </Typography>

                  <Alert severity="info" sx={{ mb: 2 }}>
                    {t('help.chart_tip', 'Hover over the chart to see exact stress values at any point in time.')}
                  </Alert>

                  <Typography variant="body2" fontWeight="bold" gutterBottom>
                    {t('help.data_grouping', 'Data Grouping Options:')}
                  </Typography>
                  <List dense>
                    <ListItem>
                      <ListItemIcon><CircleIcon sx={{ fontSize: 8 }} /></ListItemIcon>
                      <ListItemText
                        primary={t('help.grouping_average', 'Average')}
                        secondary={t('help.grouping_average_desc', 'Shows mean stress over 1-minute intervals (smoother view)')}
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon><CircleIcon sx={{ fontSize: 8 }} /></ListItemIcon>
                      <ListItemText
                        primary={t('help.grouping_max', 'Max Value')}
                        secondary={t('help.grouping_max_desc', 'Shows peak stress in 1-minute intervals (more sensitive)')}
                      />
                    </ListItem>
                  </List>

                  {/* Screenshot placeholder */}
                  <Box
                    sx={{
                      width: '100%',
                      height: 300,
                      bgcolor: theme.palette.grey[100],
                      borderRadius: 2,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      border: `2px dashed ${theme.palette.grey[300]}`,
                      mt: 2,
                    }}
                  >
                    <Typography color="text.secondary">
                      {t('help.screenshot_placeholder', '[Screenshot: Detailed Stress Chart]')}
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            {/* Hourly Heatmap */}
            <Grid item xs={12}>
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    {t('help.heatmap_title', 'Session Stress Heatmap')}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" paragraph>
                    {t('help.heatmap_desc', 'Shows stress levels broken down by hour of the day (00:00 - 23:00).')}
                  </Typography>

                  <Typography variant="body2" fontWeight="bold" gutterBottom mt={2}>
                    {t('help.heatmap_features', 'Understanding the Heatmap:')}
                  </Typography>
                  <List dense>
                    <ListItem>
                      <ListItemIcon><CheckCircleIcon color="success" fontSize="small" /></ListItemIcon>
                      <ListItemText
                        primary={t('help.heatmap_bars', '24 Vertical Bars')}
                        secondary={t('help.heatmap_bars_desc', 'One for each hour of the day')}
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon><CheckCircleIcon color="success" fontSize="small" /></ListItemIcon>
                      <ListItemText
                        primary={t('help.heatmap_colors', 'Color-Coded by Category')}
                        secondary={t('help.heatmap_colors_desc', 'Green (0-5%), Yellow (5-52.75%), Red (52.75-100%)')}
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon><CheckCircleIcon color="success" fontSize="small" /></ListItemIcon>
                      <ListItemText
                        primary={t('help.heatmap_indicator', 'Internal Progress Bar')}
                        secondary={t('help.heatmap_indicator_desc', 'Shows how far through the stress range (e.g., 36% through yellow range)')}
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon><CheckCircleIcon color="success" fontSize="small" /></ListItemIcon>
                      <ListItemText
                        primary={t('help.heatmap_peak', 'Peak Hours Highlighted')}
                        secondary={t('help.heatmap_peak_desc', 'Chips above the heatmap show hours with highest stress')}
                      />
                    </ListItem>
                  </List>

                  <Alert severity="success" sx={{ mt: 2 }}>
                    {t('help.heatmap_tip', 'Tip: Hover over any hour bar to see detailed breakdown including balanced, intensive, and dangerous time.')}
                  </Alert>

                  {/* Screenshot placeholder */}
                  <Box
                    sx={{
                      width: '100%',
                      height: 300,
                      bgcolor: theme.palette.grey[100],
                      borderRadius: 2,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      border: `2px dashed ${theme.palette.grey[300]}`,
                      mt: 2,
                    }}
                  >
                    <Typography color="text.secondary">
                      {t('help.screenshot_placeholder', '[Screenshot: Hourly Stress Heatmap]')}
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            {/* Session Selection */}
            <Grid item xs={12}>
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    {t('help.session_title', 'Session Selection & Filtering')}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" paragraph>
                    {t('help.session_desc', 'Use the dropdown menu to select and filter sessions.')}
                  </Typography>

                  <List dense>
                    <ListItem>
                      <ListItemIcon><CircleIcon sx={{ fontSize: 8 }} /></ListItemIcon>
                      <ListItemText primary={t('help.session_search', 'Search by session ID or date')} />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon><CircleIcon sx={{ fontSize: 8 }} /></ListItemIcon>
                      <ListItemText primary={t('help.session_date', 'Filter by date range')} />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon><CircleIcon sx={{ fontSize: 8 }} /></ListItemIcon>
                      <ListItemText primary={t('help.session_duration', 'Filter by duration (min/max minutes)')} />
                    </ListItem>
                  </List>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </TabPanel>

        {/* Tab 4: Tips */}
        <TabPanel value={tabValue} index={3}>
          <Grid container spacing={3}>
            {/* Alert Response */}
            <Grid item xs={12} md={6}>
              <Card variant="outlined" sx={{ height: '100%' }}>
                <CardContent>
                  <Stack direction="row" spacing={1} alignItems="center" mb={2}>
                    <WarningAmberIcon color="warning" />
                    <Typography variant="h6">
                      {t('help.alerts_response_title', 'Alert Response Guide')}
                    </Typography>
                  </Stack>

                  <Typography variant="body2" fontWeight="bold" color="warning.main" gutterBottom>
                    🟡 {t('help.yellow_alert', 'Yellow Alert (Moderate Stress)')}
                  </Typography>
                  <List dense sx={{ mb: 2 }}>
                    <ListItem>
                      <ListItemIcon><CircleIcon sx={{ fontSize: 8 }} /></ListItemIcon>
                      <ListItemText primary={t('help.yellow_step1', 'Note the time and duration')} />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon><CircleIcon sx={{ fontSize: 8 }} /></ListItemIcon>
                      <ListItemText primary={t('help.yellow_step2', 'Check if it\'s a recurring pattern')} />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon><CircleIcon sx={{ fontSize: 8 }} /></ListItemIcon>
                      <ListItemText primary={t('help.yellow_step3', 'Monitor next few sessions')} />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon><CircleIcon sx={{ fontSize: 8 }} /></ListItemIcon>
                      <ListItemText primary={t('help.yellow_step4', 'Schedule inspection if persistent')} />
                    </ListItem>
                  </List>

                  <Typography variant="body2" fontWeight="bold" color="error.main" gutterBottom>
                    🔴 {t('help.red_alert', 'Red Alert (High Stress)')}
                  </Typography>
                  <List dense>
                    <ListItem>
                      <ListItemIcon><CircleIcon sx={{ fontSize: 8 }} /></ListItemIcon>
                      <ListItemText primary={t('help.red_step1', 'Immediate review required')} />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon><CircleIcon sx={{ fontSize: 8 }} /></ListItemIcon>
                      <ListItemText primary={t('help.red_step2', 'Check detailed session view')} />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon><CircleIcon sx={{ fontSize: 8 }} /></ListItemIcon>
                      <ListItemText primary={t('help.red_step3', 'Identify specific time periods')} />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon><CircleIcon sx={{ fontSize: 8 }} /></ListItemIcon>
                      <ListItemText primary={t('help.red_step4', 'Document for maintenance team')} />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon><CircleIcon sx={{ fontSize: 8 }} /></ListItemIcon>
                      <ListItemText primary={t('help.red_step5', 'Consider immediate inspection')} />
                    </ListItem>
                  </List>
                </CardContent>
              </Card>
            </Grid>

            {/* Best Practices */}
            <Grid item xs={12} md={6}>
              <Card variant="outlined" sx={{ height: '100%' }}>
                <CardContent>
                  <Stack direction="row" spacing={1} alignItems="center" mb={2}>
                    <TipsAndUpdatesIcon color="primary" />
                    <Typography variant="h6">
                      {t('help.best_practices_title', 'Best Practices')}
                    </Typography>
                  </Stack>

                  <Typography variant="body2" fontWeight="bold" gutterBottom>
                    {t('help.daily_tasks', 'Daily Tasks')}
                  </Typography>
                  <List dense sx={{ mb: 2 }}>
                    <ListItem>
                      <ListItemIcon><CheckCircleIcon color="success" fontSize="small" /></ListItemIcon>
                      <ListItemText primary={t('help.daily_1', 'Check Activity Feed for new alerts')} />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon><CheckCircleIcon color="success" fontSize="small" /></ListItemIcon>
                      <ListItemText primary={t('help.daily_2', 'Review current day on calendar')} />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon><CheckCircleIcon color="success" fontSize="small" /></ListItemIcon>
                      <ListItemText primary={t('help.daily_3', 'Verify active sessions are normal')} />
                    </ListItem>
                  </List>

                  <Typography variant="body2" fontWeight="bold" gutterBottom>
                    {t('help.weekly_tasks', 'Weekly Tasks')}
                  </Typography>
                  <List dense sx={{ mb: 2 }}>
                    <ListItem>
                      <ListItemIcon><CheckCircleIcon color="success" fontSize="small" /></ListItemIcon>
                      <ListItemText primary={t('help.weekly_1', 'Analyze calendar patterns')} />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon><CheckCircleIcon color="success" fontSize="small" /></ListItemIcon>
                      <ListItemText primary={t('help.weekly_2', 'Review prediction trends')} />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon><CheckCircleIcon color="success" fontSize="small" /></ListItemIcon>
                      <ListItemText primary={t('help.weekly_3', 'Check for recurring issues')} />
                    </ListItem>
                  </List>

                  <Typography variant="body2" fontWeight="bold" gutterBottom>
                    {t('help.monthly_tasks', 'Monthly Tasks')}
                  </Typography>
                  <List dense>
                    <ListItem>
                      <ListItemIcon><CheckCircleIcon color="success" fontSize="small" /></ListItemIcon>
                      <ListItemText primary={t('help.monthly_1', 'Review overall vehicle performance')} />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon><CheckCircleIcon color="success" fontSize="small" /></ListItemIcon>
                      <ListItemText primary={t('help.monthly_2', 'Identify maintenance needs')} />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon><CheckCircleIcon color="success" fontSize="small" /></ListItemIcon>
                      <ListItemText primary={t('help.monthly_3', 'Compare month-to-month trends')} />
                    </ListItem>
                  </List>
                </CardContent>
              </Card>
            </Grid>

            {/* Pro Tips */}
            <Grid item xs={12}>
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    💡 {t('help.pro_tips_title', 'Pro Tips')}
                  </Typography>
                  <Grid container spacing={2} mt={1}>
                    <Grid item xs={12} md={6}>
                      <Alert severity="info">
                        {t('help.tip_1', 'Use date filters to narrow down problem periods and identify patterns more easily.')}
                      </Alert>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Alert severity="info">
                        {t('help.tip_2', 'Compare multiple sessions by switching between them to identify common patterns.')}
                      </Alert>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Alert severity="info">
                        {t('help.tip_3', 'Check predictions before planning routes to avoid high-stress periods.')}
                      </Alert>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Alert severity="info">
                        {t('help.tip_4', 'Hover over calendar days and chart points for detailed information.')}
                      </Alert>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </TabPanel>
      </Box>
    </MainLayout>
  );
}

export default Help;
